import { useEffect, useMemo, useState } from "react";
import { InfoHint } from "./InfoHint";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ResponsiveContainer, ComposedChart, Area, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from "recharts";
import { Smiley } from "@phosphor-icons/react";

export default function WellbeingCard({ days }) {
  const [entries, setEntries] = useState([]);
  const today = new Date().toISOString().slice(0, 10);
  const todayScore = entries.find((e) => e.date === today)?.score;

  useEffect(() => {
    api.get("/wellbeing").then((r) => setEntries(r.data)).catch(() => {});
  }, []);

  const save = (score) => {
    api.post("/wellbeing", { score }).then(() => {
      setEntries((e) => [...e.filter((x) => x.date !== today), { date: today, score }].sort((a, b) => a.date.localeCompare(b.date)));
      toast.success("Самочувствие записано");
    }).catch(() => toast.error("Не удалось сохранить"));
  };

  const chart = useMemo(() => {
    const byDate = Object.fromEntries(entries.map((e) => [e.date, e.score]));
    return (days || []).slice(-30).map((d) => ({ date: d.date, recovery: d.recovery, score: byDate[d.date] ?? null }));
  }, [entries, days]);

  const divergence = useMemo(() => {
    const pairs = chart.filter((c) => c.score != null && c.recovery != null).slice(-7);
    if (pairs.length < 3) return null;
    const avgFeel = pairs.reduce((s, p) => s + p.score * 10, 0) / pairs.length;
    const avgRec = pairs.reduce((s, p) => s + p.recovery, 0) / pairs.length;
    const diff = Math.round(avgFeel - avgRec);
    if (diff <= -15) return { text: `Вы чувствуете себя заметно хуже, чем показывает Whoop (${Math.round(avgFeel)} против ${Math.round(avgRec)}). Это может быть ранним сигналом стресса или выгорания.`, c: "#ffea00" };
    if (diff >= 15) return { text: `Вы чувствуете себя лучше, чем говорят данные — отличный знак, но не переусердствуйте с нагрузкой.`, c: "#00e676" };
    return { text: "Субъективное самочувствие совпадает с объективными данными — хорошая связь с телом.", c: "#00e676" };
  }, [chart]);

  const hasHistory = entries.length > 0;

  return (
    <div className="glass-card p-5" data-testid="wellbeing-card">
      <div className="flex items-center gap-2 mb-1">
        <Smiley size={18} weight="fill" className="text-[#ffea00]" />
        <h3 className="text-sm font-semibold">Как вы себя чувствуете сегодня?<InfoHint text="Ежедневная субъективная оценка самочувствия. Сопоставляется с объективными метриками Whoop, чтобы понять, что реально влияет на ваше состояние." /></h3>
      </div>
      <p className="text-xs text-muted-foreground mb-3">
        Оценивайте самочувствие каждое утро — сравним ощущения с объективным восстановлением Whoop и поймаем расхождения.
      </p>
      <div className="flex gap-1.5 flex-wrap mb-1" data-testid="wellbeing-buttons">
        {Array.from({ length: 10 }, (_, i) => i + 1).map((s) => (
          <button key={s} onClick={() => save(s)} data-testid={`wellbeing-score-${s}`}
            className={`w-8 h-8 rounded-md text-xs num font-bold transition-colors border ${
              todayScore === s
                ? "bg-[#ffea00] text-black border-[#ffea00]"
                : "bg-secondary/50 border-border/40 text-muted-foreground hover:text-foreground hover:bg-secondary"}`}>
            {s}
          </button>
        ))}
      </div>
      {divergence && <p className="text-xs mt-2" style={{ color: divergence.c }} data-testid="wellbeing-divergence">{divergence.text}</p>}
      {hasHistory && (
        <div className="mt-3">
          <ResponsiveContainer width="100%" height={160}>
            <ComposedChart data={chart}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(220 10% 16%)" vertical={false} />
              <XAxis dataKey="date" tickFormatter={(d) => `${d.slice(8, 10)}.${d.slice(5, 7)}`} stroke="hsl(220 10% 40%)" fontSize={10} tickLine={false} axisLine={false} />
              <YAxis yAxisId="rec" domain={[0, 100]} stroke="hsl(220 10% 40%)" fontSize={10} tickLine={false} axisLine={false} width={28} />
              <YAxis yAxisId="sc" orientation="right" domain={[0, 10]} stroke="hsl(220 10% 40%)" fontSize={10} tickLine={false} axisLine={false} width={24} />
              <Tooltip contentStyle={{ background: "hsl(220 10% 12%)", border: "1px solid hsl(220 10% 22%)", borderRadius: 8, fontSize: 12 }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Area yAxisId="rec" type="monotone" dataKey="recovery" name="Восстановление %" stroke="#00e676" fill="#00e676" fillOpacity={0.1} strokeWidth={1.5} connectNulls />
              <Line yAxisId="sc" type="monotone" dataKey="score" name="Самочувствие (1–10)" stroke="#ffea00" strokeWidth={2} connectNulls dot={{ r: 3 }} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
